const SEPARATORS = /[\s-]/g;

const NIP_WEIGHTS = [6, 5, 7, 2, 3, 4, 5, 6, 7];
const REGON_SHORT_WEIGHTS = [8, 9, 2, 3, 4, 5, 6, 7];
const REGON_LONG_WEIGHTS = [2, 4, 8, 5, 0, 9, 7, 3, 6, 1, 2, 4, 8];

function digitsOnly(raw: string): string {
  return raw.replace(SEPARATORS, '');
}

function hasValidControlDigit(digits: string, weights: number[]): boolean {
  const sum = weights.reduce((acc, weight, i) => acc + weight * (digits.charCodeAt(i) - 48), 0);
  const control = (sum % 11) % 10;
  return control === digits.charCodeAt(weights.length) - 48;
}

/**
 * Mirrors the server `Nip` value type: ten digits (separators ignored) with a correct checksum.
 */
export function isValidNip(raw: string): boolean {
  const value = digitsOnly(raw);
  if (value.length !== 10 || !/^\d+$/.test(value)) return false;
  const checksum =
    NIP_WEIGHTS.reduce((acc, weight, i) => acc + weight * (value.charCodeAt(i) - 48), 0) % 11;
  return checksum !== 10 && checksum === value.charCodeAt(9) - 48;
}

/**
 * Mirrors the server `Regon` value type: a 9- or 14-digit number with a correct control digit (the
 * 14-digit form also embeds a valid 9-digit prefix). Separators are ignored.
 */
export function isValidRegon(raw: string): boolean {
  const value = digitsOnly(raw);
  if (!/^\d+$/.test(value)) return false;
  if (value.length === 9) return hasValidControlDigit(value, REGON_SHORT_WEIGHTS);
  if (value.length === 14) {
    return (
      hasValidControlDigit(value.slice(0, 9), REGON_SHORT_WEIGHTS) &&
      hasValidControlDigit(value, REGON_LONG_WEIGHTS)
    );
  }
  return false;
}

/**
 * Mirrors the server `Krs` value type: up to ten digits (separators ignored), which the server
 * left-pads to the canonical ten-digit form. The register assigns no control digit.
 */
export function isValidKrs(raw: string): boolean {
  const value = digitsOnly(raw);
  return value.length > 0 && value.length <= 10 && /^\d+$/.test(value);
}

/**
 * Mirrors the server `PostalCode` rule for Poland: the canonical `XX-XXX` form, or a bare five
 * digits (which the server normalizes by inserting the dash).
 */
export function isValidPlPostalCode(raw: string): boolean {
  const code = raw.trim();
  return /^\d{2}-\d{3}$/.test(code) || /^\d{5}$/.test(code);
}

/**
 * Mirrors the server `PhoneNumber` value type: an optional leading `+` followed by 5-15 digits,
 * with the same separator set ignored (ASCII whitespace, NBSP, dots, hyphens, parentheses — not
 * the wider JS `\s`, so nothing passes here that the server would reject).
 */
export function isValidPhone(raw: string): boolean {
  const value = raw.replace(/[ \t\n\v\f\r\u00A0().-]/g, '');
  return /^\+?\d{5,15}$/.test(value);
}

export { isValidEmail } from '@/lib/validation';
